import { useMemo } from "react";
import { useSelector } from "react-redux";
import { useResolveFormSchema } from "../../../../../_hooks/useResolveFormSchema";
import fieldsToSections from "../../../../../_utils/fieldsToSections";
import { processUsageSourceFormSchema } from "../utils/usageSourceFormRules";

/**
 * Builds UsageSourceFormModal's field-schema sections from the org's
 * active "usageSource" object customizations, resolving any dynamic
 * (external reference) fields' data along the way - mirrors
 * _features/billingUnits/modals/BillingUnitFormModal/hooks/
 * useBillingUnitFormSchema.js, PLUS the databaseSelection dynamic-items
 * injection ported from the old formSchemas/useUsageSourceFormSchema.js:
 * the MongoDB-only "databaseSelection" field's `items` come from the
 * validate step's response (databaseOptions), not from object
 * customization or dynamicDataResolver - fed in here from
 * useUsageSourceValidation via the `databaseOptions` param.
 *
 * showCondition/additionalShowCondition (databaseSelection only shows for
 * originSource === "MongoDB" AND isValidated === true) are handled by the
 * shared _components/form/ engine itself (schema/evaluateCondition.js
 * already supports both) - no custom visibility filtering needed here,
 * unlike the old modal's hand-rolled shouldShowField/getFilteredSchema.
 */
export function useUsageSourceFormSchema({ databaseOptions }) {
  const objectCustomizations = useSelector(
    (state) => state?.objectCustomizationFeature?.objectCustomizations
  );

  const usageSourceCustomization = useMemo(() => {
    const customization = objectCustomizations?.data?.usageSource;
    return Array.isArray(customization) ? customization : [];
  }, [objectCustomizations]);

  const usageSourceFormSchema = useMemo(() => {
    const fields = usageSourceCustomization.filter(
      (oc) => oc.status === "active" && oc.showDuringCreation
    );
    let schema = fieldsToSections(fields);

    schema = schema.map((section) => {
      if (section.section !== "MongoDB URL") return section;

      return {
        ...section,
        fields: section.fields.map((field) => {
          if (field.name !== "databaseSelection") return field;

          return {
            ...field,
            items: databaseOptions.length > 0 ? databaseOptions.map((db) => ({ value: db, label: db })) : [],
            showCondition: { field: "originSource", operator: "equals", value: "MongoDB" },
            requiredCondition: { field: "originSource", operator: "equals", value: "MongoDB" },
            additionalShowCondition: { field: "isValidated", operator: "equals", value: true },
            isObject: true,
            valueSelector: "value",
            valueDisplay: "label",
            searchSelector: ["value", "label"],
            allowNew: false,
          };
        }),
      };
    });

    return schema;
  }, [usageSourceCustomization, databaseOptions]);

  const resolvedSchema = useResolveFormSchema(usageSourceFormSchema);

  return useMemo(() => processUsageSourceFormSchema(resolvedSchema), [resolvedSchema]);
}
